import * as _ from 'lodash';
import { combineLatest, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { Injectable } from '@angular/core';

import { AccessModel, ApiModel, CategorizedLegalRights, LegalRightsMap, RetailerAndRights } from 'models';
import { deburrAndLower, getCountryName, LegalRight, legalRightTranslationKeys } from 'helpers';
import { BrandService, SegmentService, UserService } from 'services';
import { Right } from 'shared/models/brand/types';
import { FcProductName, FcProducts, SmartRepAccessRight } from 'types';
import { logging } from 'helpers/monitoring';
import { TrackingEvents } from 'services/tracking/gtm';
import { ImageShootType } from 'app/content/types/product-images';

export const hollowShootRestrictionName = 'restrictToHollowShoot' as LegalRight;
export const hollowShootRestrictionTranslationKey = 'RIGHTS_MANAGEMENT_CHANNELS.HOLLOW_SHOOT_RESTRICTION';
const hollowShootLimitationAllowList = [
    '56f0782b7136bd4a7d42c568', // Marc O'Polo
    '56f06aa57136bd4a7d42c521', // Fashion Cloud
    '58403eeabe6fd5de4637d3c0', // PVH,
    '5b0d3d77a9a80b4f882ca107' // Just Brands
];

@Injectable()
export class RetailerAccessRightsService {
    constructor(
        private accessModel: AccessModel,
        private apiModel: ApiModel,
        private userService: UserService,
        private brandService: BrandService,
        private segmentService: SegmentService
    ) {}

    public getRetailersAndRights({
        brandIds,
        language,
        legalRights
    }: {
        brandIds: string[];
        language: string;
        legalRights?: LegalRight[];
    }): Observable<RetailerAndRights[]> {
        return combineLatest([
            this.accessModel.getRetailerRelations(brandIds, legalRights),
            this.brandService.userBrands
        ]).pipe(
            map(([retailersAndRights, brands]) =>
                _.map(retailersAndRights, (retailer) => ({
                    ...retailer,
                    address: {
                        ...retailer.address,
                        country: getCountryName(retailer.address.country, language),
                        countryCode: _.toLower(retailer.address.country)
                    },
                    isEditing: false,
                    simplifiedName: deburrAndLower(retailer.name),
                    categorizedRelations: Object.keys(retailer.relations).map((brandId) => {
                        const brand = _.find(brands, { _id: brandId });

                        return {
                            brandId,
                            brandName: brand.name,
                            categorizedLegalRights: this.categorizeLegalRights(
                                brand.fcProducts,
                                retailer.relations[brandId].legalRights,
                                retailer.relations[brandId].restrictions
                            )
                        };
                    })
                }))
            )
        );
    }

    public saveRights(brandId: string, retailerAndRights: RetailerAndRights): Observable<void> {
        const categorizedLegalRights = retailerAndRights.categorizedRelations?.find(
            ({ brandId: id }) => id === brandId
        )?.categorizedLegalRights;

        if (!categorizedLegalRights) {
            logging.info('Missing brandId in relation to be saved', null);
            return;
        }

        const productRights = categorizedLegalRights.content.product.filter(
            (right) => right.name !== hollowShootRestrictionName
        );
        const hollowShootRestriction = categorizedLegalRights.content.product.find(
            (right) => right.name === hollowShootRestrictionName
        );

        const formatLegalRights = (rights: Right[]): Partial<LegalRightsMap> =>
            _.reduce(
                rights,
                (formattedRights, right) => {
                    if (right.name === LegalRight.AutoRestock) {
                        return {
                            ...formattedRights,
                            [right.name]: {
                                value: right.selected,
                                access: right.access ?? SmartRepAccessRight.FullAccess
                            }
                        };
                    }
                    return {
                        ...formattedRights,
                        [right.name]: right.selected
                    };
                },
                {} as Partial<LegalRightsMap>
            );
        const legalRights = {
            ...formatLegalRights(categorizedLegalRights.content.marketing),
            ...formatLegalRights(productRights),
            ...formatLegalRights(categorizedLegalRights.order)
        };

        return this.apiModel
            .makeHttpObservableRequest<void>({
                method: 'POST',
                url: `${this.apiModel.getBrandsEndpoint()}/${brandId}/rights`,
                body: {
                    retailerId: retailerAndRights._id,
                    legalRights,
                    restrictions: hollowShootRestriction
                        ? {
                              shootType: hollowShootRestriction.selected ? [ImageShootType.Hollow] : []
                          }
                        : undefined
                }
            })
            .pipe(
                tap(() => {
                    const path = window.location.pathname.split('/');
                    const body = {
                        retailer: retailerAndRights._id,
                        brand: brandId,
                        access_type: Object.keys(legalRights) as LegalRight[],
                        page_type: path[path.length - 1],
                        retailer_terms: retailerAndRights.relations[brandId].termsOfUseChecked
                    };
                    this.segmentService.track(TrackingEvents.RETAILER_RIGHTS_UPDATED, body);
                    this.segmentService.trackGtm(TrackingEvents.RETAILER_RIGHTS_UPDATED, body);
                })
            );
    }

    public categorizeLegalRights(
        brandFcProducts: FcProducts,
        legalRights: LegalRightsMap = {} as LegalRightsMap,
        relationRestrictions?: { shootType?: ImageShootType[] }
    ): CategorizedLegalRights {
        const participatesInProduct = (productName: FcProductName) => brandFcProducts[productName]?.participates;

        const marketingLegalRight = participatesInProduct(FcProductName.ContentPlatform)
            ? [LegalRight.MarketingFiles]
            : [];
        const productLegalRights = participatesInProduct(FcProductName.ContentPlatform)
            ? [LegalRight.DigitalShopWindow, LegalRight.Ecommerce, LegalRight.Erp, LegalRight.ProductData]
            : [];

        const orderLegalRights: LegalRight[] = [
            ...(participatesInProduct(FcProductName.EndlessAisle) ? [LegalRight.EndlessAisle] : []),
            ...(participatesInProduct(FcProductName.OrderPlatform) ? [LegalRight.B2bPlatform] : []),
            ...(participatesInProduct(FcProductName.PreorderPlatform) ? [LegalRight.PreorderPlatform] : []),
            ...(participatesInProduct(FcProductName.AutoRestock) ? [LegalRight.AutoRestock] : [])
        ];

        const getRights = (legalRightNames: LegalRight[]) =>
            _.map(legalRightNames, (legalRightName) => {
                const right = _.get(legalRights, legalRightName);

                if (right && legalRightName === LegalRight.AutoRestock) {
                    const autoRestockRight = right as { value: boolean; access: string };
                    return {
                        name: legalRightName,
                        translationKey: legalRightTranslationKeys[legalRightName],
                        selected: autoRestockRight.value,
                        access: autoRestockRight.access
                    };
                } else {
                    return {
                        name: legalRightName,
                        translationKey: legalRightTranslationKeys[legalRightName],
                        selected: Boolean(right)
                    };
                }
            });

        const canLimitHollowShoot = _.includes(hollowShootLimitationAllowList, this.userService.getOrganizationId());

        return {
            content: {
                marketing: getRights(marketingLegalRight),
                product: [
                    ...getRights(productLegalRights),
                    ...(canLimitHollowShoot
                        ? [
                              {
                                  name: hollowShootRestrictionName,
                                  translationKey: hollowShootRestrictionTranslationKey,
                                  selected: relationRestrictions?.shootType?.includes?.(ImageShootType.Hollow) ?? false
                              }
                          ]
                        : [])
                ]
            },
            order: getRights(orderLegalRights)
        };
    }
}
